import { Fragment, useMemo } from 'react'

// Stella answers in markdown: bold figures, bullet breakdowns, headings, and
// the occasional table. The panel used to print the reply verbatim, so admins
// read the syntax ("**$4,185.25**") instead of the emphasis.
//
// This renders the subset of markdown an answer actually uses rather than
// pulling in react-markdown, which ships ESM-only and cannot be required by
// this app's Jest transform.

// Anything else — javascript:, data: — is rendered as text, since a link target
// here comes from model output.
const SAFE_HREF = /^(?:https?:\/\/|mailto:|\/)/i

const ESCAPABLE = /[\\`*_{}[\]()#+\-.!|~>]/

const LIST_ITEM = /^(\s*)([-*+]|\d{1,9}[.)])[ \t]+(.*)$/
const HEADING = /^ {0,3}(#{1,6})[ \t]+(.*?)[ \t]*#*$/
const THEMATIC_BREAK = /^ {0,3}([-*_])[ \t]*(?:\1[ \t]*){2,}$/
const FENCE = /^ {0,3}(```+|~~~+)[ \t]*([^`\s]*)/
const BLOCK_QUOTE = /^ {0,3}> ?(.*)$/
// A table needs `---|---` under its header, which also keeps a lone `---` from
// being read as one.
const TABLE_DIVIDER =
  /^ {0,3}\|?[ \t]*:?-+:?[ \t]*(\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/

const indentOf = (line) => line.length - line.trimStart().length

const isBlank = (line) => !line.trim()

// Emphasis runs longest-first so `***x***` is not read as `**` plus a stray `*`.
const EMPHASIS = [
  {
    marker: '***',
    render: (children, key) => (
      <strong key={key}>
        <em>{children}</em>
      </strong>
    ),
  },
  {
    marker: '___',
    render: (children, key) => (
      <strong key={key}>
        <em>{children}</em>
      </strong>
    ),
  },
  {
    marker: '**',
    render: (children, key) => <strong key={key}>{children}</strong>,
  },
  {
    marker: '__',
    render: (children, key) => <strong key={key}>{children}</strong>,
  },
  { marker: '~~', render: (children, key) => <del key={key}>{children}</del> },
  { marker: '*', render: (children, key) => <em key={key}>{children}</em> },
  { marker: '_', render: (children, key) => <em key={key}>{children}</em> },
]

const isWordChar = (char) => Boolean(char) && /[\w]/.test(char)

// Underscore emphasis only counts between non-word characters, so identifiers
// Stella quotes constantly (processed_at, on_sale_status) stay intact.
const canOpenUnderscore = (text, start) => !isWordChar(text[start - 1])

const findCloser = (text, from, marker) => {
  let index = text.indexOf(marker, from)
  while (index !== -1) {
    if (text[index - 1] === '\\') {
      index = text.indexOf(marker, index + marker.length)
      continue
    }
    return index
  }
  return -1
}

const parseInline = (text, keyPrefix) => {
  const nodes = []
  let buffer = ''
  let position = 0
  let keyIndex = 0

  const nextKey = () => `${keyPrefix}-${(keyIndex += 1)}`
  const flush = () => {
    if (buffer) {
      nodes.push(buffer)
      buffer = ''
    }
  }

  while (position < text.length) {
    const char = text[position]

    if (char === '\\' && ESCAPABLE.test(text[position + 1] || '')) {
      buffer += text[position + 1]
      position += 2
      continue
    }

    // Code spans win over every other marker, so `**` inside one stays literal.
    if (char === '`') {
      const fenceMatch = /^`+/.exec(text.slice(position))
      const ticks = fenceMatch[0]
      const closer = text.indexOf(ticks, position + ticks.length)
      if (closer !== -1) {
        const code = text.slice(position + ticks.length, closer)
        flush()
        nodes.push(
          <code key={nextKey()} className="draft-events-assistant__md-code">
            {code.trim()}
          </code>
        )
        position = closer + ticks.length
        continue
      }
    }

    if (char === '[') {
      const link = readLink(text, position)
      if (link) {
        flush()
        nodes.push(
          SAFE_HREF.test(link.href) ? (
            <a
              key={nextKey()}
              href={link.href}
              target="_blank"
              rel="noreferrer"
            >
              {parseInline(link.label, nextKey())}
            </a>
          ) : (
            link.label
          )
        )
        position = link.end
        continue
      }
    }

    if (char === '<') {
      const autoLink = /^<((?:https?:\/\/|mailto:)[^>\s]+)>/.exec(
        text.slice(position)
      )
      if (autoLink) {
        flush()
        nodes.push(
          <a
            key={nextKey()}
            href={autoLink[1]}
            target="_blank"
            rel="noreferrer"
          >
            {autoLink[1]}
          </a>
        )
        position += autoLink[0].length
        continue
      }
    }

    if (char === 'h' || char === 'H') {
      const bare = /^https?:\/\/[^\s<>]+/i.exec(text.slice(position))
      if (bare) {
        // Sentence punctuation after a URL belongs to the sentence.
        const href = bare[0].replace(/[.,;:!?)\]]+$/, '')
        flush()
        nodes.push(
          <a key={nextKey()} href={href} target="_blank" rel="noreferrer">
            {href}
          </a>
        )
        position += href.length
        continue
      }
    }

    if (char === '*' || char === '_' || char === '~') {
      const emphasis = EMPHASIS.find((candidate) =>
        text.startsWith(candidate.marker, position)
      )
      if (emphasis) {
        const isUnderscore = emphasis.marker[0] === '_'
        if (!isUnderscore || canOpenUnderscore(text, position)) {
          const contentStart = position + emphasis.marker.length
          const closer = findCloser(text, contentStart, emphasis.marker)
          const content = closer === -1 ? '' : text.slice(contentStart, closer)
          const closerEnd = closer + emphasis.marker.length
          const validUnderscore = !isUnderscore || !isWordChar(text[closerEnd])
          // Emphasis cannot be empty or padded, which is what keeps arithmetic
          // like "3 * 4 * 5" from collapsing into an italic run.
          if (
            closer !== -1 &&
            content.trim() &&
            !/^\s|\s$/.test(content) &&
            validUnderscore
          ) {
            flush()
            nodes.push(
              emphasis.render(parseInline(content, nextKey()), nextKey())
            )
            position = closerEnd
            continue
          }
        }
      }
    }

    buffer += char
    position += 1
  }

  flush()
  return nodes
}

// Labels can hold brackets and URLs can hold balanced parens, so both are
// matched by counting depth rather than by a regex.
const readLink = (text, start) => {
  let depth = 0
  let index = start
  for (; index < text.length; index += 1) {
    const char = text[index]
    if (char === '\\') {
      index += 1
      continue
    }
    if (char === '[') depth += 1
    if (char === ']') {
      depth -= 1
      if (depth === 0) break
    }
  }
  if (depth !== 0 || text[index + 1] !== '(') return null

  const label = text.slice(start + 1, index)
  let target = ''
  let parens = 1
  index += 2
  for (; index < text.length; index += 1) {
    const char = text[index]
    if (char === '(') parens += 1
    if (char === ')') {
      parens -= 1
      if (parens === 0) break
    }
    target += char
  }
  if (parens !== 0) return null

  // `[label](url "title")` — the title is not rendered.
  const href = target.trim().split(/\s+/)[0] || ''
  return { label, href, end: index + 1 }
}

const splitRow = (line) => {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '')
  const cells = []
  let cell = ''
  for (let index = 0; index < trimmed.length; index += 1) {
    const char = trimmed[index]
    if (char === '\\' && trimmed[index + 1] === '|') {
      cell += '|'
      index += 1
      continue
    }
    if (char === '|') {
      cells.push(cell.trim())
      cell = ''
      continue
    }
    cell += char
  }
  cells.push(cell.trim())
  return cells
}

const alignmentOf = (cell) => {
  const value = cell.trim()
  if (value.startsWith(':') && value.endsWith(':')) return 'center'
  if (value.endsWith(':')) return 'right'
  if (value.startsWith(':')) return 'left'
  return undefined
}

const readTable = (lines, start, keyPrefix) => {
  const header = splitRow(lines[start])
  const alignments = splitRow(lines[start + 1]).map(alignmentOf)
  const rows = []
  let index = start + 2
  while (index < lines.length && lines[index].includes('|')) {
    if (isBlank(lines[index])) break
    rows.push(splitRow(lines[index]))
    index += 1
  }

  return {
    next: index,
    node: (
      <div className="draft-events-assistant__md-table-scroll" key={keyPrefix}>
        <table className="draft-events-assistant__md-table">
          <thead>
            <tr>
              {header.map((cell, cellIndex) => (
                <th
                  key={cellIndex}
                  style={{ textAlign: alignments[cellIndex] }}
                >
                  {parseInline(cell, `${keyPrefix}-h-${cellIndex}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {header.map((_cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    style={{ textAlign: alignments[cellIndex] }}
                  >
                    {parseInline(
                      row[cellIndex] || '',
                      `${keyPrefix}-${rowIndex}-${cellIndex}`
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  }
}

const readList = (lines, start, keyPrefix) => {
  const first = LIST_ITEM.exec(lines[start])
  const baseIndent = first[1].length
  const ordered = /\d/.test(first[2])
  const startNumber = ordered ? parseInt(first[2], 10) : null
  const items = []
  let index = start

  while (index < lines.length) {
    const line = lines[index]

    if (isBlank(line)) {
      // A blank line only ends the list when nothing belonging to it follows.
      const following = lines[index + 1]
      const followingItem = following ? LIST_ITEM.exec(following) : null
      const continues =
        following &&
        !isBlank(following) &&
        ((followingItem && followingItem[1].length >= baseIndent) ||
          indentOf(following) > baseIndent)
      if (!continues) break
      index += 1
      continue
    }

    const match = LIST_ITEM.exec(line)
    const indent = indentOf(line)

    if (match && indent <= baseIndent + 1) {
      if (items.length && /\d/.test(match[2]) !== ordered) break
      items.push([match[3]])
      index += 1
      continue
    }

    if (!items.length) break

    // Deeper lines are the current item's own content: a nested list, or a
    // wrapped continuation of its text.
    if (indent > baseIndent) {
      items[items.length - 1].push(line.slice(Math.min(indent, baseIndent + 2)))
      index += 1
      continue
    }

    break
  }

  const Tag = ordered ? 'ol' : 'ul'
  return {
    next: index,
    node: (
      <Tag
        key={keyPrefix}
        className="draft-events-assistant__md-list"
        start={ordered && startNumber !== 1 ? startNumber : undefined}
      >
        {items.map((itemLines, itemIndex) => (
          <li key={itemIndex}>
            {itemLines.length === 1
              ? parseInline(itemLines[0], `${keyPrefix}-${itemIndex}`)
              : parseBlocks(itemLines, `${keyPrefix}-${itemIndex}`)}
          </li>
        ))}
      </Tag>
    ),
  }
}

const isTableStart = (lines, index) =>
  lines[index].includes('|') &&
  index + 1 < lines.length &&
  TABLE_DIVIDER.test(lines[index + 1])

const startsBlock = (lines, index) => {
  const line = lines[index]
  return (
    HEADING.test(line) ||
    THEMATIC_BREAK.test(line) ||
    FENCE.test(line) ||
    BLOCK_QUOTE.test(line) ||
    LIST_ITEM.test(line) ||
    isTableStart(lines, index)
  )
}

function parseBlocks(lines, keyPrefix) {
  const blocks = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index]
    const key = `${keyPrefix}-b${index}`

    if (isBlank(line)) {
      index += 1
      continue
    }

    const fence = FENCE.exec(line)
    if (fence) {
      const closing = fence[1][0].repeat(3)
      const body = []
      index += 1
      while (index < lines.length && !lines[index].trim().startsWith(closing)) {
        body.push(lines[index])
        index += 1
      }
      index += 1
      blocks.push(
        <pre className="draft-events-assistant__md-pre" key={key}>
          <code>{body.join('\n')}</code>
        </pre>
      )
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      const level = Math.min(heading[1].length + 2, 6)
      const Tag = `h${level}`
      blocks.push(
        <Tag className="draft-events-assistant__md-heading" key={key}>
          {parseInline(heading[2], key)}
        </Tag>
      )
      index += 1
      continue
    }

    if (THEMATIC_BREAK.test(line)) {
      blocks.push(<hr className="draft-events-assistant__md-rule" key={key} />)
      index += 1
      continue
    }

    if (isTableStart(lines, index)) {
      const table = readTable(lines, index, key)
      blocks.push(table.node)
      index = table.next
      continue
    }

    if (BLOCK_QUOTE.test(line)) {
      const quoted = []
      while (index < lines.length && BLOCK_QUOTE.test(lines[index])) {
        quoted.push(BLOCK_QUOTE.exec(lines[index])[1])
        index += 1
      }
      blocks.push(
        <blockquote className="draft-events-assistant__md-quote" key={key}>
          {parseBlocks(quoted, key)}
        </blockquote>
      )
      continue
    }

    if (LIST_ITEM.test(line)) {
      const list = readList(lines, index, key)
      if (list.next > index) {
        blocks.push(list.node)
        index = list.next
        continue
      }
    }

    // Whatever is left is a paragraph, running until a blank line or the start
    // of another block. Single newlines stay visible as breaks, because Stella
    // uses them to separate a figure from its caveat.
    const paragraph = []
    while (
      index < lines.length &&
      !isBlank(lines[index]) &&
      !(paragraph.length && startsBlock(lines, index))
    ) {
      paragraph.push(lines[index].trim())
      index += 1
    }
    blocks.push(
      <p className="draft-events-assistant__md-paragraph" key={key}>
        {paragraph.map((text, lineIndex) => (
          <Fragment key={lineIndex}>
            {lineIndex > 0 ? <br /> : null}
            {parseInline(text, `${key}-${lineIndex}`)}
          </Fragment>
        ))}
      </p>
    )
  }

  return blocks
}

export default function StellaMarkdown({ text, className = '' }) {
  const content = useMemo(() => {
    const source = String(text ?? '').replace(/\r\n?/g, '\n')
    if (!source.trim()) return null
    return parseBlocks(source.split('\n'), 'md')
  }, [text])

  if (!content) return null

  return (
    <div className={`draft-events-assistant__markdown ${className}`.trim()}>
      {content}
    </div>
  )
}
